import { Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import logoDark from "@/assets/logo-dark.png";

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/people/شركة-تطوير-الاستدامة-التجارية/61585370007117/", color: "hover:text-[#1877F2] hover:bg-[#1877F2]/10", label: "Visit our Facebook page" },
  { icon: Twitter, href: "https://twitter.com/Sdcgulf", color: "hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10", label: "Follow us on Twitter" },
  { icon: Instagram, href: "https://instagram.com/Sdcgulf", color: "hover:text-[#E4405F] hover:bg-[#E4405F]/10", label: "Follow us on Instagram" },
  { icon: Linkedin, href: "https://www.linkedin.com/company/sdc-sustainable-development-company/", color: "hover:text-[#0A66C2] hover:bg-[#0A66C2]/10", label: "Connect with us on LinkedIn" },
];

const footerLinks = [
  { name: "Services", href: "/services" },
  { name: "Sustainability", href: "/sustainability" },
  { name: "About", href: "/about" },
  { name: "Projects", href: "/projects" },
  { name: "Careers", href: "/careers" },
  { name: "Contact", href: "/contact" },
];

const brandLinks = [
  { name: "LEMO - Event Mobility", href: "/lemo", color: "hover:text-eco-green", internal: true },
  { name: "SOHOB - Paper Products", href: "https://sohob.sdcgulf.com/", color: "hover:text-sky-blue", internal: false },
];

const Footer = () => {
  return (
    <footer className="py-12 bg-background border-t border-border relative overflow-hidden">
      {/* Top gradient accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-eco-green via-primary to-sky-blue opacity-60" />

      {/* Background glow */}
      <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full bg-eco-green/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-sky-blue/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo & Bilingual Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4 group">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <img
                  src={logoDark}
                  alt="Sustainable Development Company logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h3 className="font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-tight">
                  Sustainable Development Company
                </h3>
                <p lang="ar" dir="rtl" className="font-arabic text-sm text-muted-foreground leading-tight">
                  شركة تطوير الاستدامة التجارية
                </p>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
              Leading the transformation of urban mobility across the Middle East with
              sustainable, innovative solutions aligned with Vision 2030.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-foreground mb-4 relative inline-block">
              Quick Links
              <span className="absolute -bottom-1 left-0 w-8 h-0.5 bg-gradient-to-r from-primary to-amber-glow rounded-full" />
            </h4>
            <ul className="space-y-2">
              {footerLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Brands */}
          <div>
            <h4 className="font-bold text-foreground mb-4 relative inline-block">
              Our Brands
              <span className="absolute -bottom-1 left-0 w-8 h-0.5 bg-gradient-to-r from-eco-green to-sky-blue rounded-full" />
            </h4>
            <ul className="space-y-2">
              {brandLinks.map((link) => (
                <li key={link.name}>
                  {link.internal ? (
                    <Link
                      to={link.href}
                      className={`text-sm text-muted-foreground ${link.color} transition-colors duration-300 flex items-center gap-2 group`}
                    >
                      <span className={`w-0 group-hover:w-2 h-0.5 ${link.name.includes('LEMO') ? 'bg-eco-green' : 'bg-sky-blue'} transition-all duration-300`} />
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm text-muted-foreground ${link.color} transition-colors duration-300 flex items-center gap-2 group`}
                    >
                      <span className={`w-0 group-hover:w-2 h-0.5 ${link.name.includes('LEMO') ? 'bg-eco-green' : 'bg-sky-blue'} transition-all duration-300`} />
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social & Contact Row */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pb-8 border-b border-border">
          <div className="flex flex-col items-center md:items-start gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Follow us <span lang="ar" dir="rtl" className="font-arabic normal-case tracking-normal">· تابعنا</span>
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="me noopener noreferrer"
                  aria-label={social.label}
                  className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-muted-foreground ${social.color} transition-all duration-300 hover:scale-110`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-muted-foreground">
            <a href="tel:+966573750548" className="hover:text-primary transition-colors">
              +966 5 737 505 48
            </a>
            <span className="hidden md:block">•</span>
            <a href="mailto:info@sdcgulf.com" className="hover:text-primary transition-colors">
              info@sdcgulf.com
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center md:text-left">
            © {new Date().getFullYear()} شركة تطوير الاستدامة التجارية (SDC). All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
